import React from 'react';
import Search from 'feather-icons-react/build/IconComponents/Search';
import X from 'feather-icons-react/build/IconComponents/X';

/**
 * SearchComponent — Shopify/shadcn-style reusable search input.
 *
 * Props:
 *   value          – search query string
 *   onChange       – input change handler (receives change event)
 *   onClear        – optional callback when clear button is clicked
 *   placeholder    – custom placeholder text (default: 'Search...')
 *   iconPlacement  – 'left' | 'right' (placement of search icon, default: 'left')
 *   isClearable    – show an 'X' clear button when value is not empty (default: true)
 *   className      – optional extra classes for the wrapper div
 *   inputClassName – optional extra classes for the input element
 *   size           – 'sm' | 'md' (default)
 */
const SearchComponent = ({
  value = '',
  onChange,
  onClear,
  placeholder = 'Search...',
  iconPlacement = 'left',
  isClearable = true,
  className = '',
  inputClassName = '',
  size = 'md',
  ...props
}) => {
  const handleClear = (e) => {
    e.preventDefault();
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { value: '' } });
    }
  };

  const hasValue = value && value.length > 0;

  const padLeft = iconPlacement === 'left' ? '34px' : '12px';
  const padRight = isClearable && hasValue ? (iconPlacement === 'left' ? '34px' : '60px') : (iconPlacement === 'left' ? '12px' : '34px');
  const inputHeight = size === 'sm' ? '32px' : '36px';

  return (
    <div className={`relative w-full flex items-center ${className}`}>
      {/* Search Icon */}
      <div
        className={`absolute top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none select-none text-[var(--table-text-color)] z-10
          ${iconPlacement === 'left' ? 'left-3' : 'right-3'}
        `}
      >
        <Search className='w-[15px] h-[15px]' />
      </div>

      {/* Input Field */}
      <input
        type='text'
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          paddingLeft: padLeft,
          paddingRight: padRight,
          height: inputHeight,
          ...props.style,
        }}
        className={`
          w-full border rounded-lg outline-none
          search-component-input text-xs sm:text-sm
          ${inputClassName}
        `}
        {...props}
      />

      {/* Clear Button */}
      {isClearable && hasValue && (
        <button
          type='button'
          onClick={handleClear}
          className='absolute top-1/2 -translate-y-1/2 flex items-center justify-center search-component-clear-btn z-10'
          style={{ right: iconPlacement === 'left' ? '10px' : '34px' }}
        >
          <X className='w-3.5 h-3.5' />
        </button>
      )}
    </div>
  );
};

export default SearchComponent;
